import { useTranslation } from 'libs/translations';

import forgeLogoWithTextSrc from 'assets/img/forgeLogoWithText.svg';
import { Icon, type IconName, cn } from 'components';
import { ENDURE_GITHUB_URL, FORGE_DOC_URL, FORGE_X_URL } from 'constants/production';
import { routes } from 'constants/routing';
import { Link } from 'containers/Link';
import { useGetMarketsPagePath } from 'hooks/useGetMarketsPagePath';
import { forwardRef } from 'react';

interface FooterProps {
  variant?: 'dark' | 'light';
  /** Landing page renders the full columned footer; app pages the compact row. */
  expanded?: boolean;
}

interface SocialLink {
  iconName: IconName;
  href?: string;
}

interface LinkConfig {
  label: string;
  href?: string;
  to?: string;
}

export const Footer = forwardRef<HTMLDivElement, FooterProps>(
  ({ variant = 'dark', expanded = false }, ref) => {
    const { t } = useTranslation();
    const { marketsPagePath } = useGetMarketsPagePath();
    const isLight = variant === 'light';

    // Discord/Telegram intentionally omitted until real URLs exist
    const socialLinks: SocialLink[] = [
      {
        iconName: 'x',
        href: FORGE_X_URL,
      },
      {
        iconName: 'github',
        href: ENDURE_GITHUB_URL,
      },
    ];

    const socialIconsDom = (
      <div className="flex flex-none items-center">
        {socialLinks.map((socialLink) => (
          <a
            className="forge-footer-social ml-4 flex h-6 w-6 items-center justify-center rounded-sm transition-colors first-of-type:ml-0"
            href={socialLink.href}
            key={socialLink.iconName}
            target="_blank"
            rel="noreferrer"
          >
            <Icon name={socialLink.iconName} className="h-3 w-3 text-inherit" />
          </a>
        ))}
      </div>
    );

    if (!expanded) {
      const footerLinks: LinkConfig[] = [
        {
          label: t('footer.links.documentation'),
          href: FORGE_DOC_URL,
        },
      ];

      return (
        <footer
          ref={ref}
          className={cn(
            'p-5 space-y-4 sm:flex sm:justify-between sm:items-center sm:space-y-0 md:px-6 xl:px-10',
            `forge-${variant}-footer`,
          )}
        >
          <div className="flex items-center gap-4 flex-wrap">
            {footerLinks.map((footerLink) => {
              const { label, ...restProps } = footerLink;

              return (
                <Link className="text-sm text-muted" key={label} {...restProps}>
                  {label}
                </Link>
              );
            })}
          </div>

          {socialIconsDom}
        </footer>
      );
    }

    const columns: { title: string; links: LinkConfig[] }[] = [
      {
        title: t('footer.columns.protocol'),
        links: [
          { label: t('footer.links.markets'), to: marketsPagePath },
          { label: t('footer.links.getStarted'), to: routes.getStarted.path },
        ],
      },
      {
        title: t('footer.columns.resources'),
        links: [
          { label: t('footer.links.documentation'), href: FORGE_DOC_URL },
          { label: t('footer.links.github'), href: ENDURE_GITHUB_URL },
        ],
      },
      {
        title: t('footer.columns.community'),
        links: [{ label: t('footer.links.x'), href: FORGE_X_URL }],
      },
    ];

    return (
      <footer ref={ref} className={cn('p-5 md:px-6 xl:px-10 py-12', `forge-${variant}-footer`)}>
        <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))]">
          <div className="space-y-4">
            <img
              src={forgeLogoWithTextSrc}
              alt={t('layout.menu.forgeLogoAlt')}
              className={cn('h-7', isLight && 'invert')}
            />

            <p className="max-w-64 text-sm text-muted">{t('footer.tagline')}</p>

            {socialIconsDom}
          </div>

          {columns.map((column) => (
            <div key={column.title} className="space-y-3">
              <p className="forge-eyebrow">{column.title}</p>

              <ul className="space-y-2">
                {column.links.map((link) => {
                  const { label, ...restProps } = link;

                  return (
                    <li key={label}>
                      <Link
                        className={cn(
                          'text-sm hover:text-foreground',
                          isLight ? 'text-muted' : 'text-grey',
                        )}
                        {...restProps}
                      >
                        {label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </footer>
    );
  },
);
